(() => {
  const resolveVariant = (variants, values) => variants.find((variant) => variant.options.length === values.length && variant.options.every((value, index) => value === values[index])) || null;

  const optionState = (variants, values, index, value) => {
    const candidates = variants.filter((variant) => variant.options[index] === value && variant.options.every((option, position) => position === index || option === values[position]));
    if (candidates.some((variant) => variant.available)) return "available";
    return candidates.length ? "sold-out" : "unavailable";
  };

  const quantityRule = (rule = {}) => {
    const min = Number.isSafeInteger(Number(rule.min)) && Number(rule.min) > 0 ? Number(rule.min) : 1;
    const increment = Number.isSafeInteger(Number(rule.increment)) && Number(rule.increment) > 0 ? Number(rule.increment) : 1;
    const max = rule.max !== null && rule.max !== undefined && rule.max !== "" && Number.isSafeInteger(Number(rule.max)) && Number(rule.max) >= min ? Number(rule.max) : null;
    return { min, increment, max };
  };

  const validQuantity = (value, rule) => {
    const quantity = Number(value);
    const bounds = quantityRule(rule);
    return value !== "" && Number.isSafeInteger(quantity) && quantity >= bounds.min && (bounds.max === null || quantity <= bounds.max) && (quantity - bounds.min) % bounds.increment === 0;
  };

  const normaliseQuantity = (value, rule) => {
    const bounds = quantityRule(rule);
    const quantity = Number(value);
    const ceiling = bounds.max === null ? Number.MAX_SAFE_INTEGER : bounds.max;
    const clamped = Math.min(ceiling, Math.max(bounds.min, Number.isFinite(quantity) ? quantity : bounds.min));
    return bounds.min + Math.floor((clamped - bounds.min) / bounds.increment) * bounds.increment;
  };

  if (typeof module !== "undefined" && module.exports) module.exports = { resolveVariant, optionState, quantityRule, validQuantity, normaliseQuantity };
  if (typeof document === "undefined") return;

  const registryKey = Symbol.for("skn.product-experience");
  if (document[registryKey]) {
    document[registryKey].mount(document);
    return;
  }

  class ProductExperience {
    constructor(section) {
      this.section = section;
      this.select = section.querySelector("[data-pdp-variant-select]");
      this.form = this.select?.form;
      this.quantity = section.querySelector("[data-pdp-quantity]");
      this.variantData = section.querySelector("[data-pdp-variants]");
      this.buttons = [...section.querySelectorAll("[data-pdp-add]")];
      this.fieldsets = [...section.querySelectorAll("[data-pdp-option-index]")];
      this.media = [...section.querySelectorAll("[data-pdp-media]")];
      this.thumbnails = [...section.querySelectorAll("[data-pdp-gallery-target]")];
      this.sticky = section.querySelector("[data-pdp-sticky]");
      this.primaryBuy = section.querySelector("[data-pdp-primary-buy]");
      this.dialog = section.querySelector("[data-pdp-zoom-dialog]");
      this.zoom = section.querySelector("[data-pdp-zoom]");
      this.zoomToggle = section.querySelector("[data-pdp-zoom-toggle]");
      this.zoomViewport = section.querySelector("[data-pdp-zoom-viewport]");
      this.error = section.querySelector("[data-pdp-error]");
      this.status = section.querySelector("[data-pdp-selection-status]");
      this.mobile = window.matchMedia("(max-width: 749px)");
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.events = new AbortController();
      this.animationFrame = null;
      this.ready = false;
      this.currentVariant = null;
      this.mediaIndex = -1;
      this.number = new Intl.NumberFormat(document.documentElement.lang || "en");
    }

    listen(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.events.signal });
    }

    init() {
      if (!this.select || !this.form || !this.quantity || !this.variantData || !this.buttons.length) return false;
      try {
        this.variants = JSON.parse(this.variantData.textContent);
      } catch {
        return false;
      }
      if (!Array.isArray(this.variants) || !this.variants.length || !this.variants.every((variant) => variant && variant.id && Array.isArray(variant.options) && variant.options.every((value) => typeof value === "string") && typeof variant.price === "string" && typeof variant.available === "boolean")) return false;
      const initialVariantId = this.select.value || this.select.dataset.initialVariantId;
      const selected = this.variants.find((variant) => String(variant.id) === initialVariantId);
      if (!selected) return false;
      if (this.fieldsets.length && (this.fieldsets.length !== selected.options.length || !this.fieldsets.every((fieldset, index) => Number(fieldset.dataset.pdpOptionIndex) === index && selected.options.every((value, position) => position !== index || [...fieldset.querySelectorAll("[data-pdp-option-value]")].some((input) => input.value === value))))) return false;

      this.listen(this.select, "change", () => {
        const variant = this.variants.find((entry) => String(entry.id) === this.select.value);
        if (variant) this.applyVariant(variant, { syncOptions: true, updateURL: true, announce: true });
      });
      this.fieldsets.forEach((fieldset) => this.listen(fieldset, "change", (event) => {
        if (!event.target.matches("[data-pdp-option-value]")) return;
        const values = this.values();
        const variant = resolveVariant(this.variants, values);
        this.applyVariant(variant, { values, updateURL: true, announce: true });
      }));
      this.listen(this.quantity, "input", () => {
        this.quantity.setCustomValidity("");
        this.quantity.removeAttribute("aria-invalid");
        this.clearError();
      });
      this.listen(this.quantity, "invalid", () => this.showQuantityError());
      this.listen(this.form, "submit", (event) => {
        if (!this.currentVariant?.available || this.select.disabled || this.select.value !== String(this.currentVariant.id)) {
          event.preventDefault();
          this.showError(this.currentVariant ? this.section.dataset.soldLabel : this.section.dataset.unavailableLabel);
          return;
        }
        if (!validQuantity(this.quantity.value, this.currentVariant.quantityRule)) {
          event.preventDefault();
          this.showQuantityError();
          this.quantity.focus();
          this.quantity.reportValidity();
        }
      });

      this.thumbnails.forEach((thumbnail) => this.listen(thumbnail, "click", (event) => {
        const index = this.media.findIndex((panel) => panel.dataset.pdpMedia === thumbnail.dataset.pdpGalleryTarget);
        if (index < 0) return;
        event.preventDefault();
        this.showMedia(index);
      }));
      this.listen(this.section.querySelector("[data-pdp-previous]"), "click", () => this.showMedia(this.mediaIndex - 1));
      this.listen(this.section.querySelector("[data-pdp-next]"), "click", () => this.showMedia(this.mediaIndex + 1));
      this.listen(this.zoom, "click", () => this.openZoom());
      this.listen(this.zoomToggle, "click", () => this.setZoom(this.zoomViewport?.dataset.zoomed !== "true"));
      this.listen(this.dialog?.querySelector(".skn-pdp__zoom-image"), "click", () => this.setZoom(this.zoomViewport?.dataset.zoomed !== "true"));
      this.listen(this.section.querySelector("[data-pdp-zoom-close]"), "click", () => this.dialog?.close());
      this.listen(this.dialog, "click", (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.listen(this.dialog, "close", () => {
        this.zoom?.focus({ preventScroll: true });
        this.queueSticky();
      });
      this.section.querySelectorAll("[data-pdp-fit-link]").forEach((link) => this.listen(link, "click", (event) => {
        const fit = this.section.querySelector("[data-pdp-fit]");
        if (!fit) return;
        event.preventDefault();
        fit.open = true;
        fit.scrollIntoView({ behavior: this.reduceMotion.matches ? "auto" : "smooth", block: "start" });
        fit.querySelector("summary")?.focus({ preventScroll: true });
      }));
      this.listen(window, "scroll", () => this.queueSticky(), { passive: true });
      this.listen(window, "resize", () => this.queueSticky(), { passive: true });
      this.listen(this.mobile, "change", () => this.queueSticky());
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(() => this.queueSticky());
        this.resizeObserver.observe(this.section);
      }

      this.applyVariant(selected, { syncOptions: true });
      const initialMedia = this.media.findIndex((panel) => panel.dataset.initialMedia === "true");
      if (this.media.length) this.showMedia(initialMedia >= 0 ? initialMedia : 0);
      this.section.querySelector("[data-pdp-gallery-controls]")?.removeAttribute("hidden");
      this.section.dataset.enhanced = "true";
      if (this.fieldsets.length) {
        this.section.querySelector("[data-pdp-option-groups]")?.removeAttribute("hidden");
        this.section.querySelector("[data-pdp-native-selector]")?.setAttribute("hidden", "");
      } else if (this.variants.length === 1) {
        this.section.querySelector("[data-pdp-native-selector]")?.setAttribute("hidden", "");
      }
      this.ready = true;
      this.queueSticky();
      return true;
    }

    values() {
      return this.fieldsets.map((fieldset) => fieldset.querySelector("[data-pdp-option-value]:checked")?.value || "");
    }

    applyVariant(variant, { values = variant?.options || this.values(), syncOptions = false, updateURL = false, announce = false } = {}) {
      const changed = this.currentVariant?.id !== variant?.id;
      this.currentVariant = variant;
      this.select.disabled = !variant;
      this.select.value = variant ? String(variant.id) : "";
      if (syncOptions) this.fieldsets.forEach((fieldset, index) => {
        fieldset.querySelectorAll("[data-pdp-option-value]").forEach((input) => { input.checked = input.value === values[index]; });
      });
      const selectionLabel = this.fieldsets.length ? this.fieldsets.map((fieldset) => fieldset.querySelector("[data-pdp-option-value]:checked")?.dataset.optionLabel || "").filter(Boolean).join(" / ") : this.select.selectedOptions[0]?.dataset.variantLabel || "";
      const availability = variant ? (variant.available ? this.section.dataset.availableLabel : this.section.dataset.soldLabel) : this.section.dataset.unavailableLabel;
      const addLabel = variant?.available ? this.section.dataset.addLabel : availability;
      this.buttons.forEach((button) => {
        button.disabled = !variant?.available;
        const text = button.querySelector("[data-pdp-add-text]");
        if (text) text.textContent = addLabel;
      });
      this.section.querySelector("[data-pdp-availability]").textContent = availability;
      this.section.querySelector("[data-pdp-sticky-selection]").textContent = selectionLabel;

      this.fieldsets.forEach((fieldset, index) => {
        const checked = fieldset.querySelector("[data-pdp-option-value]:checked");
        fieldset.querySelector("[data-pdp-option-current]").textContent = checked?.dataset.optionLabel || "";
        fieldset.querySelectorAll("[data-pdp-option-value]").forEach((input) => {
          const state = optionState(this.variants, values, index, input.value);
          input.dataset.state = state;
          const stateLabel = input.parentElement.querySelector("[data-pdp-option-state]");
          if (stateLabel) stateLabel.textContent = state === "available" ? "" : " — " + (state === "sold-out" ? this.section.dataset.soldLabel : this.section.dataset.unavailableLabel);
        });
      });
      const compare = this.section.querySelector("[data-pdp-compare-price]");
      if (variant) {
        this.section.querySelector("[data-pdp-price]").textContent = variant.price;
        this.section.querySelector("[data-pdp-sticky-price]").textContent = variant.price;
        compare.textContent = variant.comparePrice || "";
        compare.hidden = !variant.comparePrice;
        if (changed) {
          this.setQuantityRule(variant.quantityRule);
          const index = this.media.findIndex((panel) => panel.dataset.pdpMedia === String(variant.mediaId));
          if (index >= 0) this.showMedia(index);
        }
      } else {
        this.section.querySelector("[data-pdp-price]").textContent = "—";
        this.section.querySelector("[data-pdp-sticky-price]").textContent = "—";
        compare.hidden = true;
      }
      this.clearError();
      if (announce && this.status) this.status.textContent = [selectionLabel, variant?.price, availability].filter(Boolean).join(". ");
      if (updateURL) {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set("variant", variant.id);
        else url.searchParams.delete("variant");
        window.history.replaceState(window.history.state, "", url);
      }
    }

    setQuantityRule(rule) {
      const bounds = quantityRule(rule);
      this.quantity.min = bounds.min;
      this.quantity.step = bounds.increment;
      if (bounds.max === null) this.quantity.removeAttribute("max");
      else this.quantity.max = bounds.max;
      this.quantity.value = normaliseQuantity(this.quantity.value, bounds);
      this.quantity.setCustomValidity("");
      this.quantity.removeAttribute("aria-invalid");
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || "";
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = "";
    }

    showQuantityError() {
      this.quantity.setAttribute("aria-invalid", "true");
      this.quantity.setCustomValidity(this.section.dataset.quantityError);
      this.showError(this.section.dataset.quantityError);
    }

    pauseMedia(panel) {
      panel?.querySelectorAll("video").forEach((video) => video.pause());
      panel?.querySelectorAll("iframe").forEach((iframe) => {
        const url = new URL(iframe.src, window.location.href);
        if (/^(www\.)?(youtube\.com|youtube-nocookie\.com)$/.test(url.hostname)) iframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), url.origin);
        else if (url.hostname === "player.vimeo.com") iframe.contentWindow?.postMessage({ method: "pause" }, url.origin);
      });
      panel?.querySelectorAll("model-viewer").forEach((model) => {
        if (typeof model.pause === "function") model.pause();
      });
    }

    showMedia(index) {
      if (!this.media.length) return;
      const next = (index + this.media.length) % this.media.length;
      if (next !== this.mediaIndex && this.mediaIndex >= 0) this.pauseMedia(this.media[this.mediaIndex]);
      this.mediaIndex = next;
      this.media.forEach((panel, position) => { panel.hidden = position !== next; });
      const active = this.media[next];
      this.thumbnails.forEach((thumbnail) => {
        if (thumbnail.dataset.pdpGalleryTarget === active.dataset.pdpMedia) thumbnail.setAttribute("aria-current", "true");
        else thumbnail.removeAttribute("aria-current");
      });
      const counter = this.section.querySelector("[data-pdp-gallery-counter]");
      if (counter) counter.textContent = this.number.format(next + 1) + " / " + this.number.format(this.media.length);
      if (this.zoom) this.zoom.hidden = active.dataset.mediaType !== "image" || !this.dialog || typeof this.dialog.showModal !== "function";
    }

    setZoom(zoomed) {
      if (!this.zoomViewport || !this.zoomToggle) return;
      this.zoomViewport.dataset.zoomed = String(zoomed);
      this.zoomToggle.setAttribute("aria-pressed", String(zoomed));
      this.zoomToggle.textContent = zoomed ? this.zoomToggle.dataset.zoomOutLabel : this.zoomToggle.dataset.zoomInLabel;
      this.zoomViewport.scrollTop = 0;
      this.zoomViewport.scrollLeft = 0;
    }

    openZoom() {
      const panel = this.media[this.mediaIndex];
      const image = panel?.querySelector("img");
      const enlarged = this.dialog?.querySelector(".skn-pdp__zoom-image");
      if (!image || !enlarged || !this.dialog || typeof this.dialog.showModal !== "function") return;
      enlarged.removeAttribute("srcset");
      enlarged.src = panel.dataset.zoomSrc || image.currentSrc || image.src;
      enlarged.alt = image.alt;
      this.setZoom(false);
      this.dialog.showModal();
      this.queueSticky();
    }

    queueSticky() {
      if (this.animationFrame !== null) return;
      this.animationFrame = window.requestAnimationFrame(() => {
        this.animationFrame = null;
        if (!this.sticky || !this.primaryBuy) return;
        const primary = this.primaryBuy.getBoundingClientRect();
        const section = this.section.getBoundingClientRect();
        this.sticky.hidden = !this.mobile.matches || primary.bottom > 0 || section.bottom <= 100 || Boolean(this.dialog?.open);
      });
    }

    destroy() {
      this.events.abort();
      this.resizeObserver?.disconnect();
      if (this.animationFrame !== null) window.cancelAnimationFrame(this.animationFrame);
      this.media.forEach((panel) => {
        this.pauseMedia(panel);
        panel.hidden = false;
      });
      this.dialog?.close();
      if (this.sticky) this.sticky.hidden = true;
      this.section.querySelector("[data-pdp-option-groups]")?.setAttribute("hidden", "");
      this.section.querySelector("[data-pdp-native-selector]")?.removeAttribute("hidden");
      this.select.disabled = false;
      if (!this.currentVariant?.available) this.select.value = "";
      this.section.querySelector("[data-pdp-gallery-controls]")?.setAttribute("hidden", "");
      if (this.zoom) this.zoom.hidden = true;
      delete this.section.dataset.enhanced;
    }
  }

  const instances = new Map();
  const mount = (root) => {
    const sections = root.matches?.("[data-skn-product-experience]") ? [root] : [...root.querySelectorAll("[data-skn-product-experience]")];
    sections.forEach((section) => {
      if (instances.has(section)) return;
      const instance = new ProductExperience(section);
      if (instance.init()) instances.set(section, instance);
    });
  };
  document[registryKey] = { mount };
  document.addEventListener("shopify:section:load", (event) => mount(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    instances.forEach((instance, section) => {
      if (event.target === section || event.target.contains(section)) {
        instance.destroy();
        instances.delete(section);
      }
    });
  });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => mount(document), { once: true });
  else mount(document);
})();
